import { openDB } from "idb";

import { isValidJSON } from "../utils";

const DB_NAME = "deante-web-storage";
const STORE_NAME = "kv";
const MIRROR_PREFIX = "deante-idb-mirror:";
const LOCAL_STORAGE_MIRROR_MAX_SIZE = 250_000;

const dbPromise = openDB(DB_NAME, 1, {
  upgrade(db) {
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.createObjectStore(STORE_NAME);
    }
  },
});

export interface allowedKeysStorage {
  keys: "userData" | "storred_full_map_routes" | string;
}

const getStore = async () => {
  const db = await dbPromise;
  return db.transaction(STORE_NAME, "readwrite").objectStore(STORE_NAME);
};

const getMirrorKey = (key: allowedKeysStorage["keys"]) => `${MIRROR_PREFIX}${key}`;

const shouldUseLocalStorageMirror = (
  key: allowedKeysStorage["keys"],
  value?: string | null
) => {
  if (key === "bootstrapData") return false;
  if (typeof value === "string" && value.length > LOCAL_STORAGE_MIRROR_MAX_SIZE) {
    return false;
  }
  return true;
};

export const clearStorage = async () => {
  const store = await getStore();
  await store.clear();
  if (typeof window !== "undefined") {
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith(MIRROR_PREFIX))
      .forEach((key) => window.localStorage.removeItem(key));
  }
};

export const removeFromStorageByKey = async (key: allowedKeysStorage["keys"]) => {
  const store = await getStore();
  await store.delete(key);
  if (typeof window !== "undefined") {
    window.localStorage.removeItem(getMirrorKey(key));
  }
};

export const checkKeyExist = (key: allowedKeysStorage["keys"]) => {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(getMirrorKey(key)) !== null;
};

export const setDataToStorage = (key: allowedKeysStorage["keys"], data: string) => {
  if (typeof window !== "undefined") {
    if (shouldUseLocalStorageMirror(key, data)) {
      try {
        window.localStorage.setItem(getMirrorKey(key), data);
      } catch (error) {
        console.warn("Error at setDataToStorage mirror", key, error);
      }
    } else {
      window.localStorage.removeItem(getMirrorKey(key));
    }
  }

  void dbPromise
    .then(async (db) => {
      await db.put(STORE_NAME, data, key);
    })
    .catch((error) => {
      console.warn("Error at setDataToStorage", key, error);
    });
};

export const getDataFromStorage = (key: allowedKeysStorage["keys"]) => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(getMirrorKey(key));
    if (raw) {
      if (isValidJSON(raw)) {
        return JSON.parse(raw);
      }
      return raw;
    }
  } catch (error) {
    console.warn("Error at getDataFromStorage mirror", key, error);
  }

  void dbPromise
    .then(async (db) => {
      const value = await db.get(STORE_NAME, key);
      if (
        typeof window !== "undefined" &&
        typeof value === "string" &&
        shouldUseLocalStorageMirror(key, value)
      ) {
        window.localStorage.setItem(getMirrorKey(key), value);
      }
    })
    .catch((error) => {
      console.warn("Error at getDataFromStorage async sync", key, error);
    });

  return null;
};

export const getDataFromStorageAsync = async (key: allowedKeysStorage["keys"]) => {
  try {
    const db = await dbPromise;
    const value = await db.get(STORE_NAME, key);

    if (typeof window !== "undefined" && typeof value === "string") {
      if (shouldUseLocalStorageMirror(key, value)) {
        try {
          window.localStorage.setItem(getMirrorKey(key), value);
        } catch (error) {
          console.warn("Error at getDataFromStorageAsync mirror", key, error);
        }
      } else {
        window.localStorage.removeItem(getMirrorKey(key));
      }
    }

    if (value) {
      if (isValidJSON(value)) {
        return JSON.parse(value);
      }
      return value;
    }
  } catch (error) {
    console.warn("Error at getDataFromStorageAsync", key, error);
  }

  return null;
};
